import { timeToStandard } from '@frameio/utils';
import { formatBitRate, formatBytes } from '@frameio/utils/formatters';
import {
  formatBitDepth,
  formatChannels,
  formatFps,
  formatPixels,
  formatSampleRate,
} from '@frameio/utils/formatters/formatters';
import { getFormattedPageNumber } from '@frameio/utils/media';
import TEXT, {
  type Text as SystemMetadataText,
} from './generated/TEXT/systemMetadataFields';
import {
  LEGACY_SYSTEM_FIELD_KEY_BY_NAME,
  SYSTEM_FIELD_KEYS,
  SYSTEM_FIELD_TEXT_KEYS,
  type SystemFieldKey,
} from './systemMetadataFieldDefinitions';

export type { SystemFieldKey } from './systemMetadataFieldDefinitions';
export { SYSTEM_FIELD_KEYS } from './systemMetadataFieldDefinitions';

export type SystemFieldLike = {
  id: string;
  isDefaultField?: boolean | null;
  isSystemField?: boolean | null;
  name?: string | null;
};

type SystemFieldInput =
  | (Omit<SystemFieldLike, 'id'> & {
      id?: string | null;
    })
  | null
  | undefined;

const isSystemOrDefaultField = (field: SystemFieldLike) =>
  field.isSystemField || field.isDefaultField;

// Exported and typed as `unknown` so callers working with Apollo's
// DeepPartialObject — where every field including `id` is widened to
// `T | undefined` — can narrow to SystemFieldLike without a cast. The
// `id: string` contract on SystemFieldLike itself stays strict; the guard
// simply handles the boundary where the runtime value is known to have an id
// but the static type isn't narrow enough to say so.
export const isSystemFieldLike = (
  fieldOrKey: unknown
): fieldOrKey is SystemFieldLike =>
  fieldOrKey != null &&
  typeof fieldOrKey === 'object' &&
  Boolean((fieldOrKey as { id?: unknown }).id);

const isKnownSystemFieldName = (value: string): value is SystemFieldKey => {
  return Object.prototype.hasOwnProperty.call(SYSTEM_FIELD_TEXT_KEYS, value);
};

export const getSystemFieldDisplayName = (key: SystemFieldKey): string =>
  TEXT[SYSTEM_FIELD_TEXT_KEYS[key] as keyof SystemMetadataText] ?? key;

export const getSystemOrDefaultFieldKey = (
  fieldOrKey?: SystemFieldInput
): SystemFieldKey | undefined => {
  if (!fieldOrKey) return undefined;

  // For field objects, check isSystemField / isDefaultField before the name
  // lookup — prevents a custom field whose name coincidentally matches a
  // system field name from being treated as a system field key.
  if (isSystemFieldLike(fieldOrKey)) {
    if (!fieldOrKey.name || !isSystemOrDefaultField(fieldOrKey))
      return undefined;
    if (isKnownSystemFieldName(fieldOrKey.name)) return fieldOrKey.name;
    // Fall back to legacy aliases (pre-rename backend names).
    return LEGACY_SYSTEM_FIELD_KEY_BY_NAME.get(fieldOrKey.name);
  }

  // Apollo DeepPartialObject can widen `id` to undefined at runtime.
  return undefined;
};

export const isSystemOrDefaultFieldKey = (
  fieldOrKey: SystemFieldInput,
  key: SystemFieldKey
): boolean => {
  return getSystemOrDefaultFieldKey(fieldOrKey) === key;
};

export const getSystemOrDefaultFieldDisplayName = (
  fieldOrKey?: SystemFieldInput
): string | undefined => {
  if (!fieldOrKey) return undefined;

  const key = getSystemOrDefaultFieldKey(fieldOrKey);
  if (key) return getSystemFieldDisplayName(key);

  // Mirror getSystemOrDefaultFieldKey: treat missing runtime `id` as invalid.
  return isSystemFieldLike(fieldOrKey)
    ? fieldOrKey.name ?? undefined
    : undefined;
};

export const formatSystemFieldNumber = (
  value: number,
  key: SystemFieldKey
): string | undefined => {
  switch (key) {
    case SYSTEM_FIELD_KEYS.AUDIO_BIT_DEPTH:
    case SYSTEM_FIELD_KEYS.VISUAL_BIT_DEPTH:
      return formatBitDepth(value).displayValue;
    case SYSTEM_FIELD_KEYS.BIT_RATE:
    case SYSTEM_FIELD_KEYS.AUDIO_BIT_RATE:
    case SYSTEM_FIELD_KEYS.VIDEO_BIT_RATE:
      return formatBitRate(value / 8);
    case SYSTEM_FIELD_KEYS.AUDIO_SAMPLE_RATE:
      return formatSampleRate(value)?.displayValue;
    case SYSTEM_FIELD_KEYS.AUDIO_CHANNELS:
      return formatChannels(value).displayValue;
    case SYSTEM_FIELD_KEYS.DURATION:
    case SYSTEM_FIELD_KEYS.END_TIME:
    case SYSTEM_FIELD_KEYS.START_TIME:
      return timeToStandard(value);
    case SYSTEM_FIELD_KEYS.FILE_SIZE:
      return formatBytes(value);
    case SYSTEM_FIELD_KEYS.FRAME_RATE:
      return formatFps(value).displayValue;
    case SYSTEM_FIELD_KEYS.PAGE_COUNT:
      return getFormattedPageNumber(value);
    case SYSTEM_FIELD_KEYS.RESOLUTION_HEIGHT:
    case SYSTEM_FIELD_KEYS.RESOLUTION_WIDTH:
      return formatPixels(value).displayValue;
    default:
      // Fields that are numeric in the registry but need no special formatting
      // (e.g. COMMENT_COUNT, VERSION_COUNT, 3D asset counts) return undefined;
      // callers are expected to render the raw number in that case.
      return undefined;
  }
};

export const formatSystemOrDefaultFieldNumber = (
  value: number,
  fieldOrKey?: SystemFieldInput
): string | undefined => {
  const key = getSystemOrDefaultFieldKey(fieldOrKey);
  return key ? formatSystemFieldNumber(value, key) : undefined;
};
